// Admin audit log (/admin/audit) — who did what to whom, newest first.

import { Fragment, useState } from "react";
import { dataClass } from "@ahcut/ui/recipes";
import { cn } from "@/lib/cn";
import { PageHeader } from "@/components/PageHeader";
import { Loading, ErrorState, EmptyState } from "@/components/States";
import { Pagination } from "@/components/Pagination";
import { useAsync } from "@/lib/useAsync";
import { listAdminAuditLog } from "@/lib/admin";
import { auditActionLabel, formatDateTime } from "@/lib/format";
import type { AdminAuditEntry, Paged } from "@/lib/types";
import { AdminPanel } from "@/components/admin/AdminPanel";
import { DataCell, DataRow, DataTable, NoValue, type DataColumn } from "@/components/admin/DataTable";

const PAGE = 50;

// Time and actor share a column (who acted, and when), which keeps the detail
// pairs readable beside the sidebar.
const COLUMNS: readonly DataColumn[] = [
  { label: "Waktu dan aktor" },
  { label: "Aksi" },
  { label: "Target" },
  { label: "Detail" },
];

/** Detail pairs worth showing: empty strings, null and undefined are dropped. */
function detailEntries(detail: Record<string, unknown>): Array<[string, string]> {
  return Object.entries(detail)
    .filter(([, v]) => v !== "" && v !== null && v !== undefined)
    .map(([k, v]) => [k, String(v)]);
}

/** Detail as key/value pairs (was one "k: v · k: v" run-on line). */
function AuditDetail({ detail }: { detail: Record<string, unknown> }) {
  const entries = detailEntries(detail);
  if (entries.length === 0) return <NoValue label="Tanpa detail" />;
  return (
    <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-0.5 font-mono text-xs">
      {entries.map(([key, value]) => (
        <Fragment key={key}>
          <dt className="text-fg-2">{key}</dt>
          <dd className="wrap-anywhere text-fg-1">{value}</dd>
        </Fragment>
      ))}
    </dl>
  );
}

export function AdminAuditLog() {
  const [offset, setOffset] = useState(0);

  const { data, loading, error, reload } = useAsync<Paged<AdminAuditEntry>>(
    (s) => listAdminAuditLog({ limit: PAGE, offset }, s),
    [offset],
  );

  if (loading && !data) return <Loading label="Memuat audit log…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <PageHeader title="Audit" lede="Riwayat aksi admin — setiap mutasi tercatat permanen." />

      {!data || data.items.length === 0 ? (
        <EmptyState title="Belum ada aksi admin">Semua mutasi admin akan tercatat di sini.</EmptyState>
      ) : (
        <div className="flex min-w-0 flex-col gap-4">
          <AdminPanel>
            <DataTable label="Riwayat aksi admin" columns={COLUMNS} stackBelow="58rem" busy={loading}>
              {data.items.map((a) => (
                <DataRow key={a.id}>
                  <DataCell label="Waktu dan aktor" className="whitespace-nowrap">
                    <span className={cn(dataClass, "block")}>{formatDateTime(a.created_at)}</span>
                    <span className="mt-0.5 block">
                      <span className="sr-only">Aktor: </span>
                      {a.actor_email}
                    </span>
                  </DataCell>
                  <DataCell label="Aksi" className="font-medium text-fg-0">
                    {auditActionLabel(a.action)}
                  </DataCell>
                  {/* The target id stays on one line in table layout and breaks
                      only once the rows stack. Detail values break anywhere, so
                      that column gets a floor wide enough for a key plus a
                      32-character machine id (the action label wraps instead). */}
                  <DataCell label="Target" className="whitespace-nowrap">
                    <span className="block text-fg-2">{a.target_type}</span>
                    <span className={cn(dataClass, "mt-0.5 block text-xs")}>{a.target_id}</span>
                  </DataCell>
                  <DataCell label="Detail" className="min-w-[22rem]">
                    <AuditDetail detail={a.detail} />
                  </DataCell>
                </DataRow>
              ))}
            </DataTable>
          </AdminPanel>
          <Pagination
            total={data.total}
            limit={data.limit}
            offset={data.offset}
            onPage={setOffset}
            disabled={loading}
          />
        </div>
      )}
    </div>
  );
}
